import { useState } from "react";
import { Link } from "react-router-dom";
import { Flame, ArrowRight, Trophy, Share2, Video, CreditCard, LineChart, School, Zap, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import api, { formatApiError } from "@/lib/api";

/** /fusion — public landing where schools sign up for a Fusion Combine & Hit-A-Thon. */
export default function FusionLanding() {
  return (
    <div className="min-h-screen bg-[#050810] text-white" data-testid="fusion-landing-page">
      <SiteNav />

      {/* Hero */}
      <section className="pt-32 pb-16 border-b border-white/8 max-w-7xl mx-auto px-6">
        <div className="text-[10px] tracking-[0.4em] uppercase font-bold text-amber-300/90 mb-3 flex items-center gap-2">
          <Flame className="h-3.5 w-3.5" /> BioHacker Fusion™ · Combine + Hit-A-Thon
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]" data-testid="fusion-headline">
          This is not<br />
          <span className="line-through text-white/30">cookie-dough</span> fundraising.
        </h1>
        <p className="mt-6 text-white/70 max-w-3xl leading-relaxed">
          BioHacker Fusion™ is a <b className="text-white">performance + technology + fundraising experience</b>.
          Your athletes take five official swings at a Fusion Combine. Sponsors pledge per foot of their best verified
          distance — or donate flat. Every verified result feeds the athlete's permanent BioHacker performance profile.
          Your program keeps the energy, the data, and the money.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <a href="#signup" data-testid="fusion-hero-signup-cta">
            <Button className="text-[#0b1424] font-black text-sm px-6 py-6 bg-amber-400 hover:bg-amber-300">
              Sign your school up <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </a>
          <Link to="/request-demo" data-testid="fusion-hero-demo-cta">
            <Button variant="ghost" className="text-white/90 border border-white/20 hover:bg-white/10 font-bold text-sm px-6 py-6">
              Request a walkthrough
            </Button>
          </Link>
          <Link to="/fusion/login" data-testid="fusion-hero-login-cta">
            <Button variant="ghost" className="text-amber-300/90 hover:text-amber-200 hover:bg-white/5 font-bold text-sm px-6 py-6 underline underline-offset-4">
              Fusion sign in
            </Button>
          </Link>
        </div>
        <div className="mt-10 text-[10px] uppercase tracking-[0.35em] text-white/40 font-bold">
          Raise. Measure. Develop. Repeat.™
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 max-w-6xl mx-auto px-6">
        <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight leading-[1.1]">
          Five swings. One best distance.<br /><span className="text-white/50">Every pledge rides on it.</span>
        </h2>
        <div className="mt-8 grid md:grid-cols-4 gap-4" data-testid="fusion-how-it-works">
          {[
            { n: "01", icon: School, t: "School activates", b: "Upload your logo and colors once — the team campaign page and every athlete's personal fundraiser page are auto-generated and auto-branded." },
            { n: "02", icon: Share2, t: "Athletes share", b: "Each athlete gets a mobile-first campaign page with an optional AI fundraising video, QR codes, and one-tap SMS / email / social sharing." },
            { n: "03", icon: Trophy, t: "Fusion Event day", b: "Five official swings per athlete, verified by coaching staff. Best distance goes on the performance leaderboard — and into the athlete's permanent profile." },
            { n: "04", icon: CreditCard, t: "Stripe collects", b: "Per-foot pledges are calculated from the verified result and invoiced automatically. Flat donations settle instantly. Totals update live." },
          ].map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.n} className="ink-card rounded-2xl p-6">
                <div className="flex items-center justify-between mb-3">
                  <Icon className="h-6 w-6 text-amber-300" />
                  <span className="font-display font-black text-white/20 text-2xl">{s.n}</span>
                </div>
                <div className="font-display font-black text-lg">{s.t}</div>
                <p className="mt-2 text-sm text-white/65 leading-relaxed">{s.b}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* What schools get */}
      <section className="py-16 border-t border-white/8">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="font-display font-black text-3xl tracking-tight leading-[1.1]">
            What your program gets.<br /><span className="text-white/50">Beyond the check.</span>
          </h2>
          <div className="mt-8 grid md:grid-cols-3 gap-4" data-testid="fusion-benefits">
            {[
              { icon: Zap, t: "School-branded everything", b: "Your logo, your colors, your coach's message — automatically propagated across the team campaign hub and every athlete page. BioHacker Fusion™ × your brand." },
              { icon: Video, t: "AI athlete fundraising videos", b: "Consent-gated, athlete-approved AI videos where each athlete personally asks supporters to pledge — voiceover or full talking-head. Shareable by link, text, or QR." },
              { icon: CreditCard, t: "Zero collection headaches", b: "Verified sponsor billing emails, automated Stripe invoices after results, instant flat donations, and a live pledge tracker for the coach. No envelopes, no chasing." },
              { icon: LineChart, t: "Performance data that stays", b: "Fusion is event-based; BioHacker is permanent. Verified combine results — hit distance, 10-yd split, 30.6-Yard Split — Home-to-First — feed each athlete's year-round development profile." },
              { icon: Trophy, t: "Dual leaderboards", b: "Fundraising rank and performance rank, live on the team page. Competition drives sharing; sharing drives pledges." },
              { icon: Share2, t: "Built to spread", b: "Open → understand → feel connected → pledge → share. Every page is mobile-first with prominent SUPPORT THIS ATHLETE and SHARE controls." },
            ].map((c) => {
              const Icon = c.icon;
              return (
                <div key={c.t} className="ink-card rounded-2xl p-6">
                  <Icon className="h-6 w-6 text-amber-300 mb-3" />
                  <div className="font-display font-black text-lg">{c.t}</div>
                  <p className="mt-2 text-sm text-white/65 leading-relaxed">{c.b}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Signup */}
      <section id="signup" className="py-16 border-t border-white/8">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-[10px] tracking-[0.4em] uppercase font-bold text-amber-300/90 mb-3 flex items-center gap-2">
            <Flame className="h-3.5 w-3.5" /> Host a Fusion Combine & Hit-A-Thon
          </div>
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight">Sign your school up.</h2>
          <p className="mt-3 text-white/60">Create your coach account and go straight into Fusion event setup — school branding, athlete pages, and pledge collection in minutes. Our Fusion team also follows up with logistics and a revenue projection.</p>
          <SignupForm />
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

function SignupForm() {
  const [f, setF] = useState({ school_name: "", contact_name: "", role: "", email: "", phone: "", sport: "Baseball", roster_size: "", preferred_window: "", message: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // "created" | "exists"
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const cls = "bg-white/5 border-white/15 text-white placeholder:text-white/35";

  const submit = async (e) => {
    e.preventDefault();
    if (!f.school_name.trim() || !f.contact_name.trim() || !f.role.trim()) return toast.error("School, contact name and role are required");
    if (!/^[\w.\-+]+@[\w.\-]+\.[a-zA-Z]{2,}$/.test(f.email)) return toast.error("A valid email is required");
    if (f.password.length < 8) return toast.error("Password must be at least 8 characters");
    setBusy(true);
    try {
      const { data } = await api.post("/fusion/signup", { ...f, phone: f.phone || null, roster_size: f.roster_size || null, preferred_window: f.preferred_window || null, message: f.message || null });
      if (data.account_created) {
        setDone("created");
        toast.success("Account created — taking you to your Fusion event setup…");
        setTimeout(() => { window.location.href = "/dashboard/fusion"; }, 1500);
      } else {
        setDone("exists");
      }
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  if (done === "created") {
    return (
      <div className="mt-8 ink-card rounded-2xl p-10 text-center" data-testid="fusion-signup-success">
        <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-400" />
        <div className="font-display font-black text-2xl mt-4">Account created! 🔥</div>
        <p className="text-white/60 mt-2 text-sm">Taking you to your Fusion dashboard to set up your Combine & Hit-A-Thon…</p>
        <a href="/dashboard/fusion" className="inline-block mt-5 px-6 py-3 rounded-full font-black text-sm bg-amber-400 text-[#0b1424]" data-testid="signup-goto-dashboard-btn">
          Set up my event now →
        </a>
      </div>
    );
  }
  if (done === "exists") {
    return (
      <div className="mt-8 ink-card rounded-2xl p-10 text-center" data-testid="fusion-signup-exists">
        <CheckCircle2 className="h-12 w-12 mx-auto text-amber-400" />
        <div className="font-display font-black text-2xl mt-4">You already have an account</div>
        <p className="text-white/60 mt-2 text-sm">Sign in and head to your Fusion dashboard to set up your event.</p>
        <a href="/fusion/login" className="inline-block mt-5 px-6 py-3 rounded-full font-black text-sm bg-amber-400 text-[#0b1424]" data-testid="signup-goto-login-btn">
          Sign in to Fusion →
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mt-8 ink-card rounded-2xl p-6 sm:p-8 grid sm:grid-cols-2 gap-4" data-testid="fusion-signup-form">
      <Input placeholder="School / organization name *" value={f.school_name} onChange={set("school_name")} className={cls} data-testid="signup-school-input" />
      <Input placeholder="Your name *" value={f.contact_name} onChange={set("contact_name")} className={cls} data-testid="signup-contact-input" />
      <Input placeholder="Your role (AD, Head Coach…) *" value={f.role} onChange={set("role")} className={cls} data-testid="signup-role-input" />
      <Input type="email" placeholder="Work email *" value={f.email} onChange={set("email")} className={cls} data-testid="signup-email-input" />
      <Input type="password" placeholder="Create a password (8+ characters) *" value={f.password} onChange={set("password")} className={cls} data-testid="signup-password-input" />
      <Input type="tel" placeholder="Phone" value={f.phone} onChange={set("phone")} className={cls} data-testid="signup-phone-input" />
      <select value={f.sport} onChange={set("sport")} className="h-9 rounded-md px-3 text-sm bg-white/5 border border-white/15 text-white" data-testid="signup-sport-select">
        {["Baseball", "Softball", "Football", "Basketball", "Volleyball", "Soccer", "Track & Field", "Multi-sport"].map((s) => <option key={s} value={s} className="bg-[#0d1830]">{s}</option>)}
      </select>
      <Input placeholder="Roster size (e.g. 25)" value={f.roster_size} onChange={set("roster_size")} className={cls} data-testid="signup-roster-input" />
      <Input placeholder="Preferred event window (e.g. Sept 2026)" value={f.preferred_window} onChange={set("preferred_window")} className={cls} data-testid="signup-window-input" />
      <Textarea placeholder="Anything else we should know?" value={f.message} onChange={set("message")} className={`${cls} sm:col-span-2`} data-testid="signup-message-input" />
      <Button type="submit" disabled={busy} className="sm:col-span-2 bg-amber-400 text-[#0b1424] font-black hover:bg-amber-300 py-6" data-testid="signup-submit-btn">
        {busy ? "Creating your account…" : "Create account & set up my Fusion Event"} <ArrowRight className="ml-2 h-4 w-4" />
      </Button>
      <div className="sm:col-span-2 text-[11px] text-white/40 text-center">
        Your coach account is created instantly — you'll go straight to your Fusion event setup. No waiting.
      </div>
    </form>
  );
}
